import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, finalize, of, Subject, switchMap, takeUntil } from 'rxjs';
import { TSP } from '../../core/models';
import { DictionaryService } from '../../core/services/dictionary/dictionary.service';
import { ImportService } from '../../core/services/import/import.service';
import { PortalService } from '../../core/services/portal/portal.service';

type ProfileCreateFieldBase = keyof TSP.Internal.Profile.Payload.CreateProfilePayload | `customField:${number}`;
type ProfileCreateField = ProfileCreateFieldBase | '';

@Component({
  selector: 'app-import-profiles',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButton,
    MatCard,
    MatIcon,
    MatSelect,
    MatOption,
    MatProgressSpinner,
    MatFormField,
    MatLabel,
    MatCheckbox,
  ],
  templateUrl: './import-profiles.component.html',
  styleUrls: ['./import-profiles.component.scss'],
})
export class ImportProfilesComponent implements OnInit, OnDestroy {
  private readonly importService = inject(ImportService);
  private readonly portalService = inject(PortalService);
  private readonly dictionaryService = inject(DictionaryService);
  private readonly snackBar = inject(MatSnackBar);

  private readonly destroy$ = new Subject<void>();

  public readonly selectedFile = signal<File | null>(null);
  public readonly selectedFileName = computed(() => this.selectedFile()?.name ?? '');
  public readonly analyzeResult = signal<{
    total: number;
    columns: string[];
  } | null>(null);
  public readonly loading = signal(false);
  public readonly importResult = signal<{
    total: number;
    enqueued: number;
    invalid: number;
    errors: { row: number; errors: string[] }[];
  } | null>(null);

  public readonly portals = signal<TSP.Internal.Recruiter.Portal.Details[]>([]);
  public readonly portalControl = new FormControl<number | null>(null, { nonNullable: false });

  private readonly mappingControls = new Map<string, FormControl<ProfileCreateField>>();
  private readonly convertFromStringControls = new Map<ProfileCreateFieldBase, FormControl<boolean>>();

  public readonly profileStaticFields: { key: ProfileCreateFieldBase; label: string }[] = [
    { key: 'firstName', label: "Ім'я" },
    { key: 'lastName', label: 'Прізвище' },
    { key: 'middleName', label: 'По батькові' },
    { key: 'birthday', label: 'Дата народження' },
    { key: 'profileSummary', label: 'Короткий опис' },
    { key: 'insideInformation', label: 'Внутрішня інформація' },
    { key: 'salaryComment', label: 'Коментар до зарплати' },
    { key: 'currency', label: 'Валюта' },
    { key: 'city', label: 'Місто' },
    { key: 'english', label: 'Рівень англійської' },
    { key: 'level', label: 'Рівень досвіду' },
    { key: 'mainSkill', label: 'Основний навик' },
    { key: 'speciality', label: 'Спеціальність' },
    { key: 'sourceId', label: 'Джерело' },
    { key: 'rate', label: 'Ставка' },
    { key: 'rateCard', label: 'Зарплата' },
    { key: 'emails', label: 'Електронні адреси (через кому)' },
    { key: 'phones', label: 'Телефони (через кому)' },
    { key: 'telegrams', label: 'Телеграм аккаунт (через кому)' },
    { key: 'socialLinks', label: 'Соціальні мережі (через кому)' },
    { key: 'skills', label: 'Навики (через кому)' },
    { key: 'softSkills', label: "М'які навики (через кому)" },
  ];

  private readonly convertFromStringFieldKeys: ProfileCreateFieldBase[] = [
    'city',
    'english',
    'level',
    'mainSkill',
    'speciality',
    'sourceId',
  ];

  public readonly convertibleProfileFields = this.profileStaticFields.filter((field) =>
    this.convertFromStringFieldKeys.includes(field.key),
  );

  public readonly profileCustomFields = signal<{ key: ProfileCreateFieldBase; label: string }[]>([]);
  public readonly profileFields = computed(() => [...this.profileStaticFields, ...this.profileCustomFields()]);

  public ngOnInit(): void {
    this.loadPortals();

    this.portalControl.valueChanges
      .pipe(
        switchMap((portalId) => {
          if (!portalId) {
            return of([] as TSP.Internal.Dictionary.CustomField.Definition[]);
          }
          return this.dictionaryService.getProfileCustomFields(portalId).pipe(
            catchError(() => {
              this.snackBar.open('Не вдалося завантажити кастомні поля профілю.', 'OK', {
                duration: 4000,
              });
              return of([] as TSP.Internal.Dictionary.CustomField.Definition[]);
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((fields) => {
        this.profileCustomFields.set(
          fields.map((field) => ({
            key: `customField:${field.id}` as ProfileCreateFieldBase,
            label: field.name,
          })),
        );
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer?.files?.length) {
      this.handleFile(event.dataTransfer.files[0]);
    }
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.handleFile(input.files[0]);
    }
  }

  public getMappingControl(column: string): FormControl<ProfileCreateField> {
    if (!this.mappingControls.has(column)) {
      this.mappingControls.set(column, new FormControl<ProfileCreateField>(''));
    }

    return this.mappingControls.get(column);
  }

  public getConvertFromStringControl(fieldKey: ProfileCreateFieldBase): FormControl<boolean> {
    if (!this.convertFromStringControls.has(fieldKey)) {
      this.convertFromStringControls.set(fieldKey, new FormControl<boolean>(false, { nonNullable: true }));
    }

    return this.convertFromStringControls.get(fieldKey);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onMappingChange(): void {}

  public canImport(): boolean {
    if (!this.selectedFile() || !this.analyzeResult() || !this.portalControl.value) {
      return false;
    }

    const mapping = this.buildMapping();
    return Object.keys(mapping).length > 0;
  }

  public onImport(): void {
    const file = this.selectedFile();

    if (!file || !this.analyzeResult() || !this.portalControl.value) {
      return;
    }

    const mapping = this.buildMapping();
    const convertFromString = this.buildConvertFromString();

    if (!Object.keys(mapping).length) {
      this.snackBar.open('Будь ласка, вкажіть мапінг хоча б для одного поля.', 'OK', {
        duration: 3000,
      });
      return;
    }

    this.loading.set(true);

    this.importService
      .importProfiles(file, this.portalControl.value, mapping, convertFromString)
      .pipe(
        finalize(() => {
          this.loading.set(false);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (result) => {
          this.importResult.set(result);
          this.snackBar.open('Імпорт запущено.', 'OK', { duration: 3000 });
        },
        error: () => {
          this.snackBar.open('Не вдалося імпортувати профілі.', 'OK', { duration: 4000 });
        },
      });
  }

  private loadPortals(): void {
    this.portalService
      .getPortals({
        page: 0,
        size: 50,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        search: '',
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.portals.set(response.items);
      });
  }

  private handleFile(file: File): void {
    const allowedExtensions = ['.xlsx', '.xls'];

    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      this.snackBar.open('Підтримуються лише файли Excel (.xlsx, .xls).', 'OK', { duration: 4000 });
      return;
    }
    this.selectedFile.set(file);
    this.analyzeFile(file);
  }

  private analyzeFile(file: File): void {
    this.loading.set(true);
    this.importResult.set(null);

    this.importService
      .analyzeFile(file)
      .pipe(
        finalize(() => {
          this.loading.set(false);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (result) => {
          this.analyzeResult.set(result);
          this.mappingControls.clear();
        },
        error: () => {
          this.analyzeResult.set(null);
          this.mappingControls.clear();
          this.snackBar.open('Не вдалося проаналізувати файл.', 'OK', {
            duration: 4000,
          });
        },
      });
  }

  private buildMapping(): Record<string, string> {
    const mapping: Record<string, string> = {};

    if (!this.analyzeResult()) {
      return mapping;
    }

    for (const column of this.analyzeResult().columns) {
      const control = this.mappingControls.get(column);
      const value = control?.value;
      if (value) {
        mapping[column] = value;
      }
    }

    return mapping;
  }

  private buildConvertFromString(): string[] {
    const result: string[] = [];

    for (const fieldKey of this.convertFromStringFieldKeys) {
      const control = this.convertFromStringControls.get(fieldKey);
      if (control?.value) {
        result.push(fieldKey as string);
      }
    }

    return result;
  }
}
